'use client';

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCookieConsent } from '@lightwaves/hooks';

export function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const { hasDecided, acceptCookies, declineCookies } = useCookieConsent();

  // Wait for hydration before checking consent
  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    // Only show banner after hydration AND if user hasn't decided
    if (hasMounted && !hasDecided) {
      setShowBanner(true);
    } else if (hasDecided) {
      setShowBanner(false);
    }
  }, [hasMounted, hasDecided]);

  useEffect(() => {
    // Listen for the 'reopen-cookie-banner' event from the Footer
    const handleReopen = () => setShowBanner(true);
    window.addEventListener('reopen-cookie-banner', handleReopen);
    return () => window.removeEventListener('reopen-cookie-banner', handleReopen);
  }, []);

  const handleAccept = () => {
    acceptCookies();
    setShowBanner(false);
    // Reload is required to initialize Snipcart and show Shop UI
    window.location.reload();
  };

  const handleDecline = () => {
    declineCookies();
    setShowBanner(false);
    // Reload is required to purge Snipcart and hide Shop UI instantly
    window.location.reload();
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 pointer-events-none">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm pointer-events-auto"
        onClick={() => setShowBanner(false)}
      />

      {/* Banner Container */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-lg shadow-2xl p-6 pointer-events-auto border border-gray-200 dark:border-gray-800">
        <div className="flex flex-col gap-4">

          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Cookie Consent
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                We respect your privacy
              </p>
            </div>
            {/* Manual close for re-opened banner */}
            <button
              onClick={() => setShowBanner(false)}
              className="p-1 opacity-50 hover:opacity-100 transition-opacity"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Content */}
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
            <p>
              We use essential cookies to enable our online shop functionality, including:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Shopping cart management</li>
              <li>Secure checkout process</li>
              <li>Order processing</li>
            </ul>
            <p className="font-medium">
              We do <span className="underline">not</span> use analytics, tracking, or marketing cookies.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              By accepting, you allow us to use essential cookies required for e-commerce functionality.
              If you decline, the shop features will be disabled, but you can still browse our gallery.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 mt-2">
            <Button
              onClick={handleAccept}
              className="flex-1 bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200"
            >
              Accept Essential Cookies
            </Button>
            <Button
              onClick={handleDecline}
              variant="outline"
              className="flex-1 border-gray-300 dark:border-gray-700"
            >
              Decline (Disable Shop)
            </Button>
          </div>

          {/* Privacy Policy Link */}
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            Read our{' '}
            <a href="/privacy-policy" className="underline hover:text-gray-700 dark:hover:text-gray-300">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
